import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { teams } from "@/mocks/teams";

export default function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const team = teams.find((t) => t.id === teamId);
  const [seasonIndex, setSeasonIndex] = useState(0);

  const teamMatches = useMemo(() => {
    if (!team) return [];
    return matches
      .filter((m) => m.homeTeamId === team.id || m.awayTeamId === team.id)
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [team]);

  if (!team) {
    return (
      <div className="w-full px-4 md:px-6 pt-32 pb-20 min-h-[70vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-shield-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          球队信息暂缺
        </h1>
        <p className="mt-2 text-sm text-foreground-600">该球队资料尚未收录或链接有误。</p>
        <Link
          to="/category/season"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
        >
          浏览球队列表
        </Link>
      </div>
    );
  }

  const stat = team.seasonStats[seasonIndex] ?? team.seasonStats[0];

  return (
    <div className="w-full">
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.16]">
          <div className="absolute -top-20 right-10 w-72 h-72 rounded-full bg-accent-500 blur-3xl"></div>
          <div className="absolute -bottom-24 left-10 w-80 h-80 rounded-full bg-secondary-500 blur-3xl"></div>
        </div>
        <div className="relative w-full px-4 md:px-6 pt-24 md:pt-28 pb-10 md:pb-14">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <Link to="/category/season" className="hover:text-accent-400 transition-colors">
              赛事分类
            </Link>
            <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true"></i>
            <span className="text-background-100">{team.name}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <span className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-background-50 flex items-center justify-center p-4 overflow-hidden shrink-0">
              <img
                src={team.crest}
                alt={`${team.name}队徽`}
                title={`${team.name} 欧冠球队资料`}
                className="w-full h-full object-contain"
              />
            </span>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">
                {team.name}
              </h1>
              <p className="mt-1 text-sm text-background-200">
                {team.englishName} · {team.city} · {team.country}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/12 px-3 py-1 text-xs text-background-100">
                  成立 {team.founded} 年
                </span>
                <span className="rounded-full bg-white/12 px-3 py-1 text-xs text-background-100">
                  主场 {team.stadium}
                </span>
                <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                  主教练 {team.coach}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                球队历史介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {team.history}
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                {team.summary}
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-heading text-xl font-semibold text-foreground-950">
                  赛季统计数据
                </h2>
                <div className="flex flex-wrap gap-2">
                  {team.seasonStats.map((s, i) => (
                    <button
                      key={s.season}
                      type="button"
                      onClick={() => setSeasonIndex(i)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                        i === seasonIndex
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {s.season}
                    </button>
                  ))}
                </div>
              </div>

              {stat && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: "场次", value: stat.played },
                    { label: "胜", value: stat.won },
                    { label: "平", value: stat.drawn },
                    { label: "负", value: stat.lost },
                    { label: "进球", value: stat.goalsFor },
                    { label: "失球", value: stat.goalsAgainst },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-md bg-background-100 p-3 text-center"
                    >
                      <p className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        {item.value}
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
              )}
              {stat && (
                <p className="mt-4 text-sm text-foreground-600">
                  本赛季队内最佳射手：
                  <strong className="text-foreground-900">{stat.topScorer}</strong>
                </p>
              )}
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                近期比赛
              </h2>
              {teamMatches.length === 0 ? (
                <div className="rounded-md border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
                  暂无近期比赛记录
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {teamMatches.map((m) => {
                    const h = teams.find((t) => t.id === m.homeTeamId);
                    const a = teams.find((t) => t.id === m.awayTeamId);
                    const isHome = m.homeTeamId === team.id;
                    let result: { label: string; className: string } = {
                      label: "未赛",
                      className: "bg-background-200 text-foreground-600",
                    };
                    if (m.homeScore !== null && m.awayScore !== null) {
                      const my = isHome ? m.homeScore : m.awayScore;
                      const other = isHome ? m.awayScore : m.homeScore;
                      if (my > other)
                        result = {
                          label: "胜",
                          className: "bg-secondary-500 text-background-50 dark:text-foreground-950",
                        };
                      else if (my === other)
                        result = { label: "平", className: "bg-background-300 text-foreground-800" };
                      else
                        result = { label: "负", className: "bg-accent-500 text-background-50" };
                    }
                    return (
                      <Link
                        key={m.id}
                        to={`/detail/${m.id}`}
                        className="flex items-center gap-3 rounded-md border border-background-200 p-3 hover:border-primary-300 transition-colors"
                      >
                        <span
                          className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-md text-xs font-bold ${result.className}`}
                        >
                          {result.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate">
                            {h?.name} vs {a?.name}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            {m.date.replace(/-/g, "/")} · {m.stage}
                          </p>
                        </div>
                        <span className="shrink-0 font-heading text-sm font-bold text-foreground-950">
                          {m.homeScore !== null ? `${m.homeScore}:${m.awayScore}` : "VS"}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                主要球员
              </h2>
              <div className="flex flex-col gap-3">
                {team.players.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-md border border-background-200 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading text-sm font-bold">
                        {p.number}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-950 truncate">
                          {p.name}
                        </p>
                        <p className="text-xs text-foreground-500">
                          {p.position} · {p.nationality}
                        </p>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-foreground-600 leading-relaxed">{p.note}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/news"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看球队相关资讯
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                快捷入口
              </h3>
              <div className="flex flex-col gap-2">
                <Link
                  to={`/schedule/${team.seasonStats[0]?.season ?? "2025-26"}`}
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-calendar-2-line text-primary-500" aria-hidden="true"></i>
                  查看完整赛程
                </Link>
                <Link
                  to="/category/season"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-layout-grid-line text-primary-500" aria-hidden="true"></i>
                  返回赛事分类
                </Link>
                <Link
                  to="/live/m-live-01"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-live-line text-primary-500" aria-hidden="true"></i>
                  进入直播中心
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}